import { useState } from "react";
import { emptyFrame, theme } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { api } from "../api/client";

export interface InviteSummary {
  id: string;
  type: "consultancy" | "client" | "auditor" | "org_member" | "staff";
  email: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  expiresAt: string;
  redeemedAt: string | null;
  status: "pending" | "expired" | "redeemed";
  organizationName: string | null;
  consultancyName: string | null;
}

const TYPE_LABEL: Record<InviteSummary["type"], string> = {
  consultancy: "Consultancy",
  client:      "Client",
  auditor:     "Auditor",
  org_member:  "Team member",
  staff:       "Staff",
};

function context(inv: InviteSummary): string | null {
  const m = inv.metadata;
  if (inv.type === "consultancy") return inv.consultancyName ?? (m.firmName as string) ?? null;
  if (inv.type === "client")      return inv.organizationName ?? (m.orgName as string) ?? null;
  if (inv.type === "org_member")  return inv.organizationName ?? (m.orgName as string) ?? null;
  if (inv.type === "staff")       return (m.displayName as string) ?? null;
  if (inv.type === "auditor")     return (m.name as string) ?? null;
  return null;
}

/** POST /invites/:id/revoke — the link stops working; resending re-arms it. */
export function revokeInvite(id: string): Promise<unknown> {
  return api.post(`/invites/${id}/revoke`, {});
}

/** A revoked invite reads as expired on the server; the revocation rides in its metadata. */
const isRevoked = (inv: InviteSummary) => typeof inv.metadata?.revokedAt === "string";

function StatusBadge({ status, revoked }: { status: InviteSummary["status"]; revoked?: boolean }) {
  const map: Record<InviteSummary["status"], { tone: StatusTone; label: string }> = {
    pending:  { tone: "partial", label: "Pending" },
    expired:  { tone: "none", label: "Expired" },
    redeemed: { tone: "met", label: "Accepted" },
  };
  const { tone, label: base } = map[status];
  const label = revoked && status !== "redeemed" ? "Revoked" : base;
  return <StatusTag tone={tone}>{label}</StatusTag>;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const th: React.CSSProperties = {
  padding: "8px 12px", textAlign: "left", fontSize: 10,
  color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600,
};
const td: React.CSSProperties = { padding: "10px 12px", fontSize: 13 };

function errorMessage(err: unknown, fallback: string): string {
  const payload = (err as { payload?: { error?: unknown } })?.payload;
  return typeof payload?.error === "string" ? payload.error : fallback;
}

export interface InviteHistoryTableProps {
  invites: InviteSummary[];
  /** Called after a successful resend (and revoke) so the caller can reload. */
  onResent?: () => void;
  /**
   * Sends the invite again. Defaults to POST /invites/:id/resend; the auditor
   * portal passes its bureau-scoped route instead.
   */
  onResend?: (id: string) => Promise<unknown>;
  /**
   * Revokes a pending or expired invite. When present, a "Revoke" button is
   * shown on those rows; the table asks for confirmation before calling it.
   */
  onRevoke?: (id: string) => Promise<unknown>;
}

export function InviteHistoryTable({
  invites,
  onResent,
  onResend,
  onRevoke,
}: InviteHistoryTableProps) {
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function clearError(id: string) {
    setErrors((e) => { const n = { ...e }; delete n[id]; return n; });
  }

  async function handleResend(id: string) {
    setResendingId(id);
    clearError(id);
    try {
      if (onResend) await onResend(id);
      else await api.post(`/invites/${id}/resend`, {});
      onResent?.();
    } catch (err: unknown) {
      setErrors((e) => ({ ...e, [id]: errorMessage(err, "Failed to resend") }));
    } finally {
      setResendingId(null);
    }
  }

  async function handleRevoke(inv: InviteSummary) {
    if (!onRevoke) return;
    if (!confirm(`Revoke the invitation for ${inv.email}? The link they were sent will stop working.`)) return;
    setRevokingId(inv.id);
    clearError(inv.id);
    try {
      await onRevoke(inv.id);
      onResent?.();
    } catch (err: unknown) {
      setErrors((e) => ({ ...e, [inv.id]: errorMessage(err, "Failed to revoke") }));
    } finally {
      setRevokingId(null);
    }
  }

  if (invites.length === 0) {
    return (
      <div style={{ ...emptyFrame, padding: "32px 16px", fontSize: 13, color: theme.color.faint }}>
        No invites sent yet.
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt }}>
            <th style={th}>Email</th>
            <th style={th}>Type</th>
            <th style={th}>Name / Context</th>
            <th style={th}>Sent</th>
            <th style={th}>Status</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {invites.map((inv, i) => {
            const isLast = i === invites.length - 1;
            const ctx = context(inv);
            const canResend = inv.status !== "redeemed";
            const canRevoke = !!onRevoke && inv.status !== "redeemed" && !isRevoked(inv);
            const busy = resendingId === inv.id || revokingId === inv.id;
            return (
              <tr
                key={inv.id}
                style={{ borderBottom: isLast ? "none" : `1px solid ${theme.color.border}` }}
              >
                <td style={{ ...td, color: theme.color.heading, fontWeight: 500 }}>{inv.email}</td>
                <td style={td}>
                  <span style={{
                    fontSize: 11, padding: "2px 7px", borderRadius: 4, fontWeight: 600,
                    background: theme.color.accentBg, color: theme.color.accentText,
                  }}>
                    {TYPE_LABEL[inv.type]}
                  </span>
                </td>
                <td style={{ ...td, color: theme.color.subtle }}>{ctx ?? "—"}</td>
                <td style={{ ...td, color: theme.color.faint }}>{fmtDate(inv.createdAt)}</td>
                <td style={td}><StatusBadge status={inv.status} revoked={isRevoked(inv)} /></td>
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  {errors[inv.id] && (
                    <span style={{ fontSize: 11, color: theme.color.dangerText, marginRight: 8 }}>
                      {errors[inv.id]}
                    </span>
                  )}
                  {canResend && (
                    <button
                      onClick={() => handleResend(inv.id)}
                      disabled={busy}
                      style={{
                        fontSize: 11, padding: "4px 10px", borderRadius: 3,
                        border: `1px solid ${theme.color.border}`,
                        background: "transparent", color: theme.color.muted,
                        cursor: busy ? "default" : "pointer",
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {resendingId === inv.id ? "Sending…" : "Resend"}
                    </button>
                  )}
                  {canRevoke && (
                    <button
                      onClick={() => handleRevoke(inv)}
                      disabled={busy}
                      style={{
                        fontSize: 11, padding: "4px 10px", borderRadius: 3, marginLeft: 6,
                        border: `1px solid ${theme.color.border}`,
                        background: "transparent", color: theme.color.dangerText,
                        cursor: busy ? "default" : "pointer",
                        opacity: busy ? 0.5 : 1,
                      }}
                    >
                      {revokingId === inv.id ? "Revoking…" : "Revoke"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
